import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi, downloadBlob, uploadImage } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Eye, Send, Download, Users, Save, Loader2, X, Image as ImageIcon } from "lucide-react";

export default function AdminMailingForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const [mid, setMid] = useState(id || null);
  const [form, setForm] = useState({ title: "", subject: "", content_html: "", discount_text: "", coupon_code: "", status: "draft", month: "" });
  const [topic, setTopic] = useState("");
  const [coupons, setCoupons] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const insertImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const path = await uploadImage(f);
      const abs = path.startsWith("http") ? path : `${process.env.REACT_APP_BACKEND_URL}${path}`;
      setForm((fm) => ({ ...fm, content_html: `${fm.content_html}\n<p style="text-align:center"><img src="${abs}" alt="" style="max-width:100%;height:auto;border-radius:8px" /></p>` }));
      toast.success("Imagen añadida al contenido");
    } catch (err) {
      toast.error("No se pudo subir la imagen");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  useEffect(() => {
    adminApi.get("/admin/coupons").then((r) => setCoupons(r.data)).catch(() => {});
    if (id) adminApi.get(`/admin/mailings/${id}`).then((r) => {
      const m = r.data;
      setForm({ title: m.title || "", subject: m.subject || "", content_html: m.content_html || "", discount_text: m.discount_text || "", coupon_code: m.coupon_code || "", status: m.status || "draft", month: m.month || "" });
    });
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    if (!topic.trim()) { toast.error("Escribe un tema o instrucciones para la IA"); return; }
    setGenerating(true);
    try {
      const r = await adminApi.post("/admin/mailings/generate", { topic, discount_text: form.discount_text, coupon_code: form.coupon_code, month: form.month });
      setForm((f) => ({ ...f, subject: r.data.subject || f.subject, content_html: r.data.content_html || f.content_html }));
      toast.success("Contenido generado con IA");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al generar"); }
    setGenerating(false);
  };

  const save = async () => {
    if (!form.subject.trim()) { toast.error("El asunto es obligatorio"); return; }
    setSaving(true);
    try {
      if (mid) {
        await adminApi.put(`/admin/mailings/${mid}`, form);
        toast.success("Mailing guardado");
      } else {
        const r = await adminApi.post("/admin/mailings", form);
        setMid(r.data.id);
        toast.success("Mailing creado");
        nav(`/admin/mailings/${r.data.id}/editar`, { replace: true });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    setSaving(false);
  };

  const openPreview = async () => {
    if (!mid) { toast.error("Guarda el mailing primero"); return; }
    const r = await adminApi.get(`/admin/mailings/${mid}/preview`);
    setPreview(r.data.html);
  };

  const sendTest = async () => {
    if (!mid) { toast.error("Guarda el mailing primero"); return; }
    try {
      const r = await adminApi.post(`/admin/mailings/${mid}/test`);
      toast.success(`Prueba enviada a ${r.data.email}`);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo enviar la prueba"); }
  };

  return (
    <div className="max-w-5xl" data-testid="mailing-form">
      <button onClick={() => nav("/admin/mailings")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-brand mb-4"><ArrowLeft className="h-4 w-4" /> Volver a mailings</button>
      <h1 className="font-heading font-bold text-3xl tracking-tighter mb-6">{mid ? "Editar mailing" : "Nuevo mailing"}</h1>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* AI */}
          <div className="bg-white border border-border rounded-sm p-4">
            <p className="font-heading font-semibold mb-2 flex items-center gap-2"><Sparkles className="h-4 w-4 text-brand" /> Generar con IA</p>
            <textarea value={topic} onChange={(e) => setTopic(e.target.value)} rows={2} placeholder="Ej: Boletín de junio con novedades en bandejas y una oferta del 10% en moldes" className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="ai-topic" />
            <button onClick={generate} disabled={generating} className="mt-2 inline-flex items-center gap-2 bg-brand-ink text-white rounded-sm px-3 py-2 text-sm hover:opacity-90 disabled:opacity-60" data-testid="ai-generate-btn">
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generar contenido
            </button>
          </div>

          <div className="bg-white border border-border rounded-sm p-4 space-y-3">
            <div>
              <label className="text-sm font-medium block mb-1">Título interno</label>
              <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Boletín junio 2026" className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="mailing-title" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Asunto del email *</label>
              <input value={form.subject} onChange={(e) => set("subject", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="mailing-subject" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium">Contenido (HTML simple)</label>
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1 text-xs border border-border rounded-sm px-2 py-1 hover:border-brand disabled:opacity-50" data-testid="insert-image-btn">
                  {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageIcon className="h-3 w-3" />} Insertar imagen
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={insertImage} data-testid="mailing-image-input" />
              </div>
              <textarea value={form.content_html} onChange={(e) => set("content_html", e.target.value)} rows={12} className="w-full px-3 py-2 text-sm border border-border rounded-sm font-mono" data-testid="mailing-content" />
              <p className="text-xs text-muted-foreground mt-1">Usa etiquetas simples: &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;&lt;li&gt;, &lt;strong&gt;. La cabecera, el botón y el pie se añaden automáticamente.</p>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          <div className="bg-white border border-border rounded-sm p-4 space-y-3">
            <p className="font-heading font-semibold text-sm">Descuento</p>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Texto de la oferta (productos o servicios)</label>
              <input value={form.discount_text} onChange={(e) => set("discount_text", e.target.value)} placeholder="10% en moldes este mes" className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid="mailing-discount-text" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Cupón asociado (opcional)</label>
              <select value={form.coupon_code} onChange={(e) => set("coupon_code", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm bg-white" data-testid="mailing-coupon">
                <option value="">— Sin cupón —</option>
                {coupons.map((c) => <option key={c.id} value={c.code}>{c.code} ({c.type === "percent" ? `${c.value}%` : `${c.value}€`})</option>)}
              </select>
            </div>
          </div>

          <div className="bg-white border border-border rounded-sm p-4 space-y-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Estado</label>
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm bg-white" data-testid="mailing-status">
                <option value="draft">Borrador</option>
                <option value="ready">Listo para enviar</option>
              </select>
            </div>
            <button onClick={save} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-brand text-white py-2.5 rounded-sm font-semibold hover:bg-brand-dark disabled:opacity-60" data-testid="save-mailing-btn">
              <Save className="h-4 w-4" /> {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>

          <div className="bg-white border border-border rounded-sm p-4 space-y-2">
            <p className="font-heading font-semibold text-sm">Revisar y enviar</p>
            <button onClick={openPreview} disabled={!mid} className="w-full inline-flex items-center justify-center gap-2 border border-border rounded-sm py-2 text-sm hover:border-brand disabled:opacity-50" data-testid="preview-btn"><Eye className="h-4 w-4" /> Vista previa</button>
            <button onClick={sendTest} disabled={!mid} className="w-full inline-flex items-center justify-center gap-2 border border-border rounded-sm py-2 text-sm hover:border-brand disabled:opacity-50" data-testid="test-send-btn"><Send className="h-4 w-4" /> Enviar prueba a mi email</button>
            <div className="border-t border-border pt-2 mt-1 space-y-2">
              <p className="text-xs text-muted-foreground">Para enviar a todos los clientes (gratis con <a href="https://resend.com/broadcasts" target="_blank" rel="noreferrer" className="underline text-brand">Resend Broadcasts</a>):</p>
              <button onClick={() => mid && downloadBlob(`/admin/mailings/${mid}/recipients`, "destinatarios.csv")} disabled={!mid} className="w-full inline-flex items-center justify-center gap-2 border border-border rounded-sm py-2 text-sm hover:border-brand disabled:opacity-50" data-testid="dl-recipients-btn"><Users className="h-4 w-4" /> 1. Descargar destinatarios (CSV)</button>
              <button onClick={() => mid && downloadBlob(`/admin/mailings/${mid}/download`, "mailing.html")} disabled={!mid} className="w-full inline-flex items-center justify-center gap-2 border border-border rounded-sm py-2 text-sm hover:border-brand disabled:opacity-50" data-testid="dl-html-btn"><Download className="h-4 w-4" /> 2. Descargar HTML</button>
              <p className="text-[11px] text-muted-foreground leading-snug">Sube el CSV como <em>Audience</em> y pega el HTML en un <em>Broadcast</em> de Resend para enviarlo desde tu dominio.</p>
            </div>
          </div>
        </div>
      </div>

      {preview !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="preview-modal">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <p className="font-heading font-semibold">Vista previa del email</p>
              <button onClick={() => setPreview(null)}><X className="h-5 w-5" /></button>
            </div>
            <iframe title="preview" srcDoc={preview} className="flex-1 w-full min-h-[500px]" />
          </div>
        </div>
      )}
    </div>
  );
}
